import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Custom hook for the send-documents-for-electronic-signature feature flag
 *
 * @returns {Object} Object containing the feature flag state and utility functions
 * @returns {boolean} returns.isEnabled - Whether the feature is enabled
 * @returns {boolean} returns.canSendForSignature - Whether documents can be sent for electronic signature
 * @returns {Function} returns.shouldShowSignatureButton - Function to determine if signature button should be shown
 * @returns {Function} returns.shouldShowSignatureModal - Function to determine if signature modal should be shown
 */
const useSendDocumentsForElectronicSignature = () => {
  const fflags = useFlags();
  const { sendDocumentsForElectronicSignature } = fflags;

  // Default to false if flag is not defined
  const isEnabled = sendDocumentsForElectronicSignature ?? false;

  // Utility function to determine if signature button should be shown
  const shouldShowSignatureButton = (additionalConditions = {}) => {
    const {
      hasPortal = true,
      isComplete = false,
      isSubmitting = false,
    } = additionalConditions;

    return isEnabled && hasPortal && isComplete && !isSubmitting;
  };

  // Utility function to determine if signature modal should be shown
  const shouldShowSignatureModal = (additionalConditions = {}) => {
    const { hasPortal = true, isComplete = false } = additionalConditions;

    return isEnabled && hasPortal && isComplete;
  };

  // Utility function to check if documents can be sent for signature
  const canSendForSignature = (additionalConditions = {}) => {
    const {
      hasPortal = true,
      isComplete = false,
      isSubmitting = false,
    } = additionalConditions;

    return isEnabled && hasPortal && isComplete && !isSubmitting;
  };

  return {
    isEnabled,
    canSendForSignature,
    shouldShowSignatureButton,
    shouldShowSignatureModal,
  };
};

export default useSendDocumentsForElectronicSignature;
